import { useState } from 'react';
import { createApiUrl } from '../config/api';
import type { DataResponse } from '../types/dataResponse';

export interface UseFileUploadOptions {
  sessionId: string | null;
  userID: string | null;
  onResponse?: (data: DataResponse[]) => void;
  maxFiles?: number;
  overwrite?: boolean;
  skipTabCreation?: boolean;
}

export function useFileUpload({ sessionId, userID, onResponse, maxFiles, overwrite = false, skipTabCreation = false }: UseFileUploadOptions) {
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  // Append new files to attachedFiles, avoiding duplicates
  const handleFilesSelected = (newFiles: File[]) => {
    setAttachedFiles(prev => {
      const existingNames = new Set(prev.map(f => f.name));
      const filesToAdd = newFiles.filter(f => !existingNames.has(f.name));
      
      // If maxFiles is set, limit the total number of files
      if (maxFiles !== undefined) {
        const availableSlots = maxFiles - prev.length;
        if (availableSlots <= 0) return prev;
        return [...prev, ...filesToAdd.slice(0, availableSlots)];
      }
      
      return [...prev, ...filesToAdd];
    });
  };

  const handleRemoveFile = (name: string) => {
    setAttachedFiles(prev => prev.filter(f => f.name !== name));
  };

  // Upload files to backend
  const uploadFiles = async (userMessage: string = '') => {
    if (!attachedFiles.length) return { success: true, data: null };
    
    setIsUploading(true);
    const formData = new FormData();
    
    attachedFiles.forEach((file) => {
      formData.append("files", file);
    });
    
    formData.append("session_id", sessionId ?? "");
    formData.append("user_id", userID ?? "");
    formData.append("overwrite", overwrite ? "true" : "false");
    formData.append("skip_tab_creation", skipTabCreation ? "true" : "false");
    formData.append("user_message", userMessage);
    

    try {
      const response = await fetch(createApiUrl("/file_handling/upload_from_user"), {
        method: "POST",
        body: formData,
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Upload failed with response:', errorText);
        throw new Error(`Upload failed: ${response.status} ${response.statusText} - ${errorText}`);
      }
      
      const responseData = await response.json();
      
      // Backend now returns DataResponse[] format
      if (Array.isArray(responseData) && responseData.length > 0) {
        console.log('Upload response data:', responseData);
        onResponse?.(responseData);
      }
      
      setIsUploading(false);
      return { success: true, data: responseData };
    } catch (e) {
      console.error('Upload error details:', e);
      setIsUploading(false);
      alert("File upload failed. Please try again.");
      return { success: false, data: null };
    }
  };

  const clearFiles = () => {
    setAttachedFiles([]);
  };

  return {
    attachedFiles,
    isUploading,
    handleFilesSelected,
    handleRemoveFile,
    uploadFiles,
    clearFiles
  };
}